import { defineComponent as _defineComponent } from 'vue'
import { computed, ref } from 'vue';
import CButton from '@/components/shared/CButton';
import CInput from '@/components/shared/CInput';
import CCheckbox from '@/components/shared/CCheckbox.vue';
import MaskSelectorModal from '../MaskSelectorModal.vue';
import { useToaster } from '@/composables/toaster';
import { MaskType } from '@/utils/enums/maskType.enum';
import CalendarColorField from './CalendarColorField.vue';
import CalendarFieldTitle from './CalendarFieldTitle.vue';
import {
    CALENDAR_MARKER_OPTIONS,
    CUSTOM_MARKER_MAX_KB,
    DEFAULT_CUSTOM_MARKER_SCALE,
    DEFAULT_CALENDAR_MARKER,
    findPresetMarker,
    getCalendarMarkerPresets,
    loadCustomMarker,
    renderMarkerThumbnail
} from './calendarUtils';
import { useCalendarConfig } from './useCalendarConfig';
import type { MaskDto } from '@/api/typings/mask';
import type { ImagePlaceholderElement } from '@/typings/customilyElements/canvasElements';
import type { CalendarConfig } from './types';

// MaskType is a const enum, so the template can't reference it directly

export default /*#__PURE__*/_defineComponent({
  __name: 'SelectedDateProperties',
  props: {
    imageElement: null
  },
  emits: ["update-config"],
  setup(__props: any, { emit }: { emit: ({
    (e: 'update-config', config: Partial<CalendarConfig>): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as {
    imageElement?: ImagePlaceholderElement;
};

const MARKER_MASK_TYPE = MaskType.CalendarMarker;

// Vue 2.7 SFC compiler cannot resolve imported types in defineProps



const { config, elementKey, disabled, update } = useCalendarConfig(props, emit);
const toaster = useToaster();

const modalOpen = ref(false);
const markerPresets = ref<MaskDto[]>([]);

const isCustomMarker = computed(() => config.value?.markerType === 'custom' && !!config.value.customMarker);

const presetMarker = computed(() =>
    CALENDAR_MARKER_OPTIONS.find((option) => option.value === config.value?.markerType)
);

const markerThumbnail = computed(() =>
    config.value ? renderMarkerThumbnail(config.value.markerType, config.value.customMarker) : ''
);

const openModal = () => {
    if (disabled.value) return;
    // Built on first open, measuring the markers needs the DOM
    if (!markerPresets.value.length) markerPresets.value = getCalendarMarkerPresets();
    modalOpen.value = true;
};

const onMarkerSelected = async (mask: MaskDto) => {
    modalOpen.value = false;

    const preset = findPresetMarker(mask);
    if (preset) {
        update({ markerType: preset, customMarker: '', customMarkerPath: '' });
        return;
    }

    try {
        update(await loadCustomMarker(mask.path, currentDayFontSize(), config.value?.customMarkerScale));
    } catch (error) {
        toaster.error(`The marker ${mask.name} could not be loaded: ${(error as Error).message}`);
    }
};

// Read from the element, the local copy misses font size changes made in the Days section
const currentDayFontSize = () => props.imageElement?.calendarConfig?.dayFontSize ?? config.value?.dayFontSize ?? '';

const onMarkerScaleChange = async (value: string | number = 0) => {
    const scale = Number(value);
    const path = config.value?.customMarkerPath;
    if (!path || !(scale > 0)) return;

    try {
        update(await loadCustomMarker(path, currentDayFontSize(), scale));
    } catch (error) {
        toaster.error(`The marker could not be resized: ${(error as Error).message}`);
    }
};

const removeCustomMarker = () => {
    update({
        markerType: DEFAULT_CALENDAR_MARKER,
        customMarker: '',
        customMarkerPath: '',
        customMarkerScale: undefined
    });
};

const onMarkerColorChange = (color: string) => {
    update({ markColor: color, markBorderColor: color });
};

return { __sfc: true,MARKER_MASK_TYPE, props, emit, config, elementKey, disabled, update, toaster, modalOpen, markerPresets, isCustomMarker, presetMarker, markerThumbnail, openModal, onMarkerSelected, currentDayFontSize, onMarkerScaleChange, removeCustomMarker, onMarkerColorChange, CButton, CInput, CCheckbox, MaskSelectorModal, CalendarColorField, CalendarFieldTitle, CUSTOM_MARKER_MAX_KB, DEFAULT_CUSTOM_MARKER_SCALE }
}

})